/* eslint-disable prettier/prettier */
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CloseIcon from '@mui/icons-material/Close'
import EditNoteIcon from '@mui/icons-material/EditOutlined'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Switch,
    TextField,
    Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import EmailBuilder from '../../components/EmailBuilder/EmailBuilder'
import {
    emptyDesign,
    designFromEmailContent,
    designFromLegacyHtml,
    isBhanzuDesign,
} from '../../components/EmailBuilder/blocks'
import { compileDesign } from '../../components/EmailBuilder/mjml'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import SingleSelect from '../../components/SingleSelect'
import {
    CATEGORIES,
    categoriesForCommType,
    subCategoriesForCategory,
    subCategoryLabel,
    validateCategoryPair,
} from '../../helpers/templateCategories'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import AiAssistant from './AiAssistant'
import ErrorBanner from '../../components/shared/ErrorBanner'
import TagMismatchPanel from '../../components/TagMismatchPanel'
import SenderEmails from '../../components/SenderEmails'
import { commTypes } from '../../helpers/Constants'
import { channelFrom } from '../../helpers/aiHandoff'
import { BUILT_IN_VARIABLES } from '../../helpers/testSend'
import {
    EMAIL,
    PUSH,
    SLACK,
    SMS,
    VOICE,
    WHATSAPP,
    VARIABLE_NAME_RE,
    buildTextTemplatePayload,
    declaredVariables,
    editPayload,
    inChannelStyle,
    normaliseTemplateName,
    validateTemplateForm,
    variableExample,
    whatsappVariables,
} from '../../helpers/templateForm'
import {
    WA_MEDIA,
    buildMetaTemplateData,
    buttonRowsFromAi,
    buttonsForAi,
    emptyWhatsAppDraft,
    isMediaHeader,
    whatsappDraftProblems,
} from '../../helpers/whatsappTemplate'
import { errorMessage } from '../../services/axios/errorMessage'
import useFileHandler from '../../hooks/useFileHandler'
import TemplateService from '../../services/templateService'
import {
    ACCENT,
    BRAND,
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    TEXT,
    cardSx,
    pageSx,
} from '../../theme/tokens'
import { withLineBreaksForSend } from '../../components/TemplateRenderer'
import { findSavedTemplate } from '../../helpers/templateLookup'
import axiosInstance from '../../services/axios/axiosConfig'
import { endpoints } from '../../services/axios/endpoints'
import EmailHtmlPanel from './EmailHtmlPanel'
import WhatsAppContent from './WhatsAppContent'

// Plain-content types share the simple textarea flow.
const TEXT_CONTENT_TYPES = [SMS, VOICE, PUSH, SLACK]

// No Authentication: WhatsApp registers an authentication template only with
// its preset body and a one-time-password button carrying an `otp_type`, and
// the AWS registration (`_build_components`) builds neither, so it would be
// refused at review. Offer it again once the backend builds that shape.
// WhatsApp registration through AWS offers Utility and Marketing only (the
// Authentication shape, a one-time-password button, is not built yet).
const waCategoryOptions = (commType) =>
    categoriesForCommType(commType)
        .filter((c) => c.value !== 'AUTHENTICATION')
        .reverse() // Marketing first, as the registration form always listed them

// The WhatsApp account a template sends from (the old app's "Choose
// Channel"). A pipeline takes it from its template, and the sender picks the
// number by it; with none, everything went through `default`.
const WA_CHANNELS = [
    { id: 1, label: 'Engineering', value: 'default' },
    { id: 2, label: 'Marketing', value: 'dce-12' },
    { id: 3, label: 'People Ops', value: 'people-ops' },
]

// How a new email is made: in the block builder, or from an HTML file.
const BUILDER = 'builder'
const HTML_FILE = 'html'

// What the email sender uses when a template sets none
// (lambda-comms-internal-trigger templates/email_template.py), so a new
// template starts with them, as in the old app.
const DEFAULT_SOURCE_EMAIL = 'no-reply@bhanzu.com'
const DEFAULT_SOURCE_EMAIL_HEADER = 'Bhanzu'

const NO_WA_MARKS = { header: '', buttonRows: {} }

/**
 * A saved template's body text: the S3 file's contents the API read for us,
 * or the body itself when it is stored inline (older SMS and WhatsApp rows).
 */
const storedBody = (row) => {
    if (typeof row?.extracted_body === 'string') return row.extracted_body
    const body = row?.body ?? ''
    return body.startsWith('s3://') ? '' : body
}

/** A chosen file's text (FileReader: `File.text()` is missing from older engines and jsdom). */
const readText = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result ?? ''))
        reader.onerror = () => reject(reader.error)
        reader.readAsText(file)
    })

/** The builder design saved with an email template, or null when it has none the builder can open. */
const storedDesign = (row) => {
    let design = row?.extracted_design_json ?? null
    if (typeof design === 'string') {
        try {
            design = JSON.parse(design)
        } catch {
            design = null
        }
    }
    return isBhanzuDesign(design) ? design : null
}

// A contained button: the theme gives it Deep Blue on orange (white was
// 3.3:1), the lighter hover and the disabled look.
const actionButtonSx = {
    fontWeight: 600,
    px: 3,
}

/** A card's eyebrow title (the type scale's overline slot). */
const SectionTitle = ({ children }) => (
    <Typography
        variant="overline"
        component="div"
        sx={{ color: TEXT.muted, lineHeight: 2, mb: 2 }}
    >
        {children}
    </Typography>
)

SectionTitle.propTypes = { children: PropTypes.node.isRequired }

/**
 * Create a template, or edit one when `templateId` is given.
 *
 * One form for both, as in the old app: editing shows every field the
 * template has, filled from GET /comms/v2/template/{id}, and saves with
 * PUT /comms/v2/template/{id} (the saved row with the changes on top, see
 * editPayload). The channel and the name cannot change on an edit: the name
 * is how templates are also looked up, and the update does not check it is
 * free. A WhatsApp template is not edited here: its content is registered
 * with WhatsApp, and the update cannot re-register it yet.
 *
 * A new email is built in the block builder or made from an HTML file
 * (`uploadHtml` starts on the file, which is where the library's Upload
 * Template lands); either way every email field and the name check come
 * along. A WhatsApp template's header media is uploaded first and the
 * template is created only once that upload has a stored path. Cancel goes
 * back, asking first when something on the page changed.
 */
const TemplateForm = ({ templateId, uploadHtml = false }) => {
    const isEdit = Boolean(templateId)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { handleFileRead } = useFileHandler()
    const [templateName, setTemplateName] = useState('')
    const [commType, setCommType] = useState(EMAIL)
    const [comment, setComment] = useState('')
    const [subject, setSubject] = useState('')
    // Field errors keyed by field ({templateName, subject, approverType,
    // text, dltTemplateId}); each clears as soon as that field is edited.
    const [fieldErrors, setFieldErrors] = useState({})
    const [dltTemplateId, setDltTemplateId] = useState('')
    const [attachments, setAttachments] = useState([])
    const [approverType, setApproverType] = useState('')
    const { approverTypes } = useSelector((state) => state.template)
    const [text, setText] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')
    // The service refused the save because the category and sub-category do not fit the template (what to
    // change comes with it); cleared when either is edited.
    const [tagMismatch, setTagMismatch] = useState(null)
    const [design, setDesign] = useState(emptyDesign)
    const [emailSource, setEmailSource] = useState(
        uploadHtml ? HTML_FILE : BUILDER
    )
    // WhatsApp registration fields: category, sending account, and the
    // header, footer, buttons and examples (helpers/whatsappTemplate).
    // What kind of message this is (BHAN-2774); required for every channel.
    const [category, setCategory] = useState('')
    const [subCategory, setSubCategory] = useState('')
    const [waChannel, setWaChannel] = useState('default')
    const [waDraft, setWaDraft] = useState(emptyWhatsAppDraft)
    // What the last WhatsApp submit found wrong, to outline those fields.
    const [waMarks, setWaMarks] = useState(NO_WA_MARKS)
    // The header media already uploaded for this file ({file, media}), so a
    // retry after a failed create does not store the file a second time.
    const uploadedMedia = useRef(null)
    // Email sending: who it is from, where replies go, the calendar invite.
    const [sourceEmail, setSourceEmail] = useState(DEFAULT_SOURCE_EMAIL)
    const [sourceEmailHeader, setSourceEmailHeader] = useState(
        DEFAULT_SOURCE_EMAIL_HEADER
    )
    const [replyTo, setReplyTo] = useState('')
    const [isCalendar, setIsCalendar] = useState(false)
    const [calendarTitle, setCalendarTitle] = useState('')
    const [senderEmails, setSenderEmails] = useState([])
    const [managingSenders, setManagingSenders] = useState(false)
    // Variables added by hand (the content's own are found in it).
    const [extraVariables, setExtraVariables] = useState([])
    const [variableDraft, setVariableDraft] = useState('')
    const [variableError, setVariableError] = useState('')
    // Edit only: the saved row, and how loading it went.
    const [row, setRow] = useState(null)
    const [load, setLoad] = useState({
        state: isEdit ? 'loading' : 'ready',
        reason: '',
    })
    // An email whose content is an HTML file: on an edit, the stored HTML of
    // an email with no builder design (shown read-only); the HTML of a file
    // chosen to replace it, or to make a new email from.
    const [legacyHtml, setLegacyHtml] = useState(null)
    const [replacementHtml, setReplacementHtml] = useState(null)
    const [replacementName, setReplacementName] = useState('')
    const [confirmLeave, setConfirmLeave] = useState(false)

    const waVariables = whatsappVariables(commType === WHATSAPP ? text : '')
    const htmlEmail =
        commType === EMAIL &&
        (isEdit ? legacyHtml !== null : emailSource === HTML_FILE)
    const emailHtml = replacementHtml ?? legacyHtml ?? ''
    // The content the declared variables are read from.
    const variableTexts =
        commType === EMAIL
            ? [
                  subject,
                  isCalendar ? calendarTitle : '',
                  htmlEmail ? emailHtml : JSON.stringify(design),
              ]
            : [commType === PUSH ? subject : '', text]
    const variables = declaredVariables({
        commType,
        texts: variableTexts,
        extra: extraVariables,
    })
    const foundVariables = declaredVariables({
        commType,
        texts: variableTexts,
    })

    // Everything the user can change, to tell whether Cancel loses anything.
    const snapshot = JSON.stringify([
        templateName,
        commType,
        comment,
        subject,
        dltTemplateId,
        attachments.map((a) => a.name ?? a.file_name),
        approverType,
        text,
        design,
        emailSource,
        category,
        subCategory,
        waChannel,
        { ...waDraft, mediaFile: waDraft.mediaFile?.name ?? null },
        sourceEmail,
        sourceEmailHeader,
        replyTo,
        isCalendar,
        calendarTitle,
        extraVariables,
        replacementHtml,
    ])
    // The form as it was when it became ready (a new one's defaults, or the
    // template as loaded); null until then.
    const baseline = useRef(null)
    useEffect(() => {
        if (load.state === 'ready' && baseline.current === null)
            baseline.current = snapshot
    }, [load.state, snapshot])
    const changed = baseline.current !== null && baseline.current !== snapshot

    useEffect(() => {
        const state = location.state
        if (isEdit) return
        // `?channel=` comes from the pipeline wizard's "Create a template"
        // link, which opens this page in a new tab (no router state there).
        const channel =
            state?.channel ||
            channelFrom(
                new URLSearchParams(location.search).get('channel'),
                commTypes.map((c) => c.value),
                ''
            )
        if (channel) setCommType(channel)
        if (!state) return
        if (state.subject) setSubject(state.subject)
        if (state.body) {
            setText(state.body)
            // Email bodies handed over as HTML (AI variants, legacy
            // duplicates) become an editable raw-HTML block.
            if (state.channel === EMAIL || /<[a-z][\s\S]*>/i.test(state.body))
                setDesign(designFromLegacyHtml(state.body))
        }
    }, [])

    /** The sender addresses to offer (the saved one stays even if no longer listed). */
    const loadSenderEmails = () =>
        dispatch(TemplateService.getSenderEmails()).then((resp) => {
            if (resp.error) return
            const list = resp.payload?.data?.sender_emails ?? []
            setSenderEmails(list.map((entry) => entry?.email).filter(Boolean))
        })

    // The approver list's first load: the select says so under it (its menu
    // fills in when the list arrives, even if already open).
    const [loadingApprovers, setLoadingApprovers] = useState(true)

    useEffect(() => {
        dispatch(TemplateService.getApproverTypes()).then(() =>
            setLoadingApprovers(false)
        )
        loadSenderEmails()
    }, [])

    /** Fill the form from the saved template (edit). */
    const loadTemplate = () => {
        setLoad({ state: 'loading', reason: '' })
        dispatch(TemplateService.getTemplate(templateId)).then((resp) => {
            const saved = resp.error ? null : resp.payload?.data?.template
            if (!saved) {
                setLoad({
                    state: 'error',
                    reason: errorMessage({ data: resp.payload }),
                })
                return
            }
            const savedType = String(saved.comm_type ?? '').toLowerCase()
            setRow(saved)
            setCommType(savedType)
            setTemplateName(saved.template_name ?? '')
            setComment(saved.comment ?? '')
            setSubject(saved.subject ?? '')
            setApproverType(saved.approver_type ?? '')
            setCategory(saved.category ?? '')
            setSubCategory(saved.sub_category ?? '')
            setDltTemplateId(saved.dlt_template_id ?? '')
            setAttachments(
                Array.isArray(saved.attachments) ? saved.attachments : []
            )
            setExtraVariables(
                Array.isArray(saved.others)
                    ? saved.others.filter((v) => typeof v === 'string')
                    : []
            )
            setSourceEmail(saved.source_email || DEFAULT_SOURCE_EMAIL)
            setSourceEmailHeader(
                saved.source_email_header || DEFAULT_SOURCE_EMAIL_HEADER
            )
            setReplyTo(saved.reply_to ?? '')
            setIsCalendar(Boolean(saved.is_calendar))
            setCalendarTitle(saved.calendar_title ?? '')
            setText(storedBody(saved))
            if (savedType === EMAIL) {
                const savedDesign = storedDesign(saved)
                if (savedDesign) setDesign(savedDesign)
                else setLegacyHtml(storedBody(saved))
            }
            setLoad({ state: 'ready', reason: '' })
        })
    }

    useEffect(() => {
        if (isEdit) loadTemplate()
    }, [templateId])

    /** Set a field's value and drop its error, so the red outline goes as the user fixes it. */
    const editField = (field, setter) => (value) => {
        setter(value)
        if (field === 'category' || field === 'subCategory')
            setTagMismatch(null)
        setFieldErrors((prev) => {
            if (!prev[field]) return prev
            const next = { ...prev }
            delete next[field]
            return next
        })
    }

    const readTagMismatch = (payload) =>
        payload?.error_object?.kind === 'tag_mismatch'
            ? payload.error_object
            : null

    /** Show what the service recommends, and mark the field(s) it says do not fit. */
    const showTagMismatch = (detail) => {
        setTagMismatch(detail)
        setFieldErrors((prev) => ({
            ...prev,
            ...(detail.category.fits
                ? {}
                : {
                      category: `Suggested: ${
                          CATEGORIES.find(
                              (c) => c.value === detail.category.recommended
                          )?.label ?? detail.category.recommended
                      }`,
                  }),
            ...(detail.sub_category.fits
                ? {}
                : {
                      subCategory: `Suggested: ${subCategoryLabel(
                          detail.sub_category.recommended
                      )}`,
                  }),
        }))
    }

    /** Take the service's recommendation for both fields; the same submit button then checks it again. */
    const useSuggestedTags = () => {
        if (!tagMismatch) return
        setCategory(tagMismatch.recommended.category)
        setSubCategory(tagMismatch.recommended.sub_category)
        setTagMismatch(null)
        setFieldErrors((prev) => {
            const next = { ...prev }
            delete next.category
            delete next.subCategory
            return next
        })
    }

    const onChangeCommType = (value) => {
        setCommType(value)
        // The WhatsApp rule is stricter: what is typed must still fit it.
        setTemplateName((name) => normaliseTemplateName(value, name))
        // The text stays, with its variables in the new channel's style:
        // {{amount}} in an SMS would go out as is.
        setText((current) => inChannelStyle(current, value))
        setFieldErrors({})
        setApproverType('')
        setCategory('')
        setSubCategory('')
        setSubmitError('')
        setWaMarks(NO_WA_MARKS)
    }

    // A banner about the body ("Template body is required.") or the examples
    // goes as soon as the user edits them (it stayed after the fix).
    const changeText = (value) => {
        setSubmitError('')
        editField('text', setText)(value)
    }
    const handleTextChange = (event) => changeText(event.target.value)

    /**
     * Apply a change to the WhatsApp draft. `cleared` names the marks this
     * edit may clear: the header's, one button row's, or every row's (a
     * removed row renumbers the rest).
     */
    const changeWaDraft = (patch, cleared = {}) => {
        setSubmitError('')
        setWaDraft((prev) => ({ ...prev, ...patch }))
        setWaMarks((prev) => {
            let next = prev
            if (cleared.header && next.header) next = { ...next, header: '' }
            if (cleared.buttonRows) next = { ...next, buttonRows: {} }
            if (
                cleared.buttonRow !== undefined &&
                next.buttonRows[cleared.buttonRow]
            ) {
                const rows = { ...next.buttonRows }
                delete rows[cleared.buttonRow]
                next = { ...next, buttonRows: rows }
            }
            return next
        })
    }

    /** Back to where the user came from (the library when the form was opened directly). */
    const leave = () =>
        location.key === 'default' ? navigate('/templates') : navigate(-1)

    const onCancel = () => (changed ? setConfirmLeave(true) : leave())

    /**
     * After a create that got no answer (timeout, network): was it saved
     * anyway? Returns the template id, or null. A slow dev API answered after
     * the ingress gave up, the page said "not created", and a retry made a
     * duplicate.
     */
    const savedDespiteNoAnswer = async (payload) => {
        try {
            const res = await axiosInstance.get(endpoints.getTemplates)
            return findSavedTemplate(res?.data, {
                name: payload.template_name,
                commType: payload.comm_type,
            })
        } catch {
            return null
        }
    }

    /** Save the edit: the saved row with `changes` on top (editPayload). */
    const updateTemplate = (changes) => {
        setSubmitting(true)
        setSubmitError('')
        setTagMismatch(null)
        dispatch(
            TemplateService.updateTemplate({
                templateID: templateId,
                payload: editPayload(row, changes),
            })
        ).then((resp) => {
            setSubmitting(false)
            if (resp.error) {
                const mismatch = readTagMismatch(resp.payload)
                if (mismatch) {
                    showTagMismatch(mismatch)
                    return
                }
                // A PUT of the same row is safe to repeat, so a lost answer
                // only needs another try.
                setSubmitError(
                    resp.payload?.noResponse
                        ? 'The server did not answer in time, so the change may not be saved. Save again.'
                        : `The template was not saved: ${errorMessage({
                              data: resp.payload,
                          })}`
                )
                return
            }
            navigate('/templates')
        })
    }

    const submitTemplate = (taglessPayload, onSuccess) => {
        const payload = {
            ...taglessPayload,
            category,
            sub_category: subCategory,
        }
        if (isEdit) {
            updateTemplate(payload)
            return
        }
        setSubmitting(true)
        setSubmitError('')
        setTagMismatch(null)
        dispatch(TemplateService.createTemplate(payload)).then(async (resp) => {
            if (resp.error && resp.payload?.noResponse) {
                setSubmitError(
                    'The server did not answer in time; checking whether it was saved.'
                )
                const savedID = await savedDespiteNoAnswer(payload)
                setSubmitting(false)
                if (savedID) {
                    setSubmitError('')
                    onSuccess({ payload: { data: { template_id: savedID } } })
                    return
                }
                setSubmitError(
                    'The template was not created: the server did not answer and no template with this name exists. Try again.'
                )
                return
            }
            setSubmitting(false)
            if (resp.error) {
                const mismatch = readTagMismatch(resp.payload)
                if (mismatch) {
                    showTagMismatch(mismatch)
                    return
                }
                setSubmitError(
                    `The template was not created: ${errorMessage({
                        data: resp.payload,
                    })}`
                )
                return
            }
            onSuccess(resp)
        })
    }

    /** A new email opens its test page (v2 answers flat: {status, message, template_id}). */
    const toTestPage = (resp) =>
        navigate(
            `/test-template/${encodeURIComponent(
                resp.payload.data.template_id
            )}/${encodeURIComponent(templateName.trim())}/${commType}`
        )

    /**
     * Store the WhatsApp header's file (POST /comms/v2/files/upload) and
     * return {s3Path, fileName, fileType}, or null after saying why it
     * failed. The same file is stored once, however often the create is
     * retried.
     */
    const uploadHeaderMedia = async (file) => {
        if (uploadedMedia.current?.file === file)
            return uploadedMedia.current.media
        const label = WA_MEDIA[waDraft.headerType].label
        setSubmitting(true)
        setSubmitError('')
        const resp = await dispatch(TemplateService.uploadWhatsAppMedia(file))
        const s3Path = resp.error ? '' : resp.payload?.data?.s3_path
        if (typeof s3Path !== 'string' || !s3Path.startsWith('s3://')) {
            setSubmitting(false)
            setSubmitError(
                `The header ${label} was not uploaded, so the template was not created: ${
                    resp.error
                        ? errorMessage({ data: resp.payload })
                        : 'the upload answered without a stored file.'
                } Try again.`
            )
            return null
        }
        const media = { s3Path, fileName: file.name, fileType: file.type }
        uploadedMedia.current = { file, media }
        return media
    }

    /**
     * Check a WhatsApp template, upload its header media, then create it
     * (registered through AWS End User Messaging; WhatsApp reviews it).
     */
    const submitWhatsApp = async (basePayload) => {
        const nameValid = !!basePayload.template_name
        const approverValid = !!approverType
        const tagErrors = validateCategoryPair(category, subCategory, commType)
        const categoryValid = Object.keys(tagErrors).length === 0
        setFieldErrors({
            ...(nameValid ? {} : { templateName: 'Enter a template name' }),
            ...(approverValid
                ? {}
                : { approverType: 'Choose who approves this template' }),
            ...tagErrors,
        })
        const problems = whatsappDraftProblems({
            draft: waDraft,
            body: text,
            variables: waVariables,
        })
        setWaMarks({ header: problems.header, buttonRows: problems.buttonRows })
        setSubmitError(problems.messages.join(' '))
        if (
            !nameValid ||
            !approverValid ||
            !categoryValid ||
            problems.messages.length
        )
            return
        let media = null
        if (isMediaHeader(waDraft.headerType)) {
            media = await uploadHeaderMedia(waDraft.mediaFile)
            if (!media) return
        }
        submitTemplate(
            {
                ...basePayload,
                template_data: text.trim(),
                approver_type: approverType,
                channel: waChannel,
                // The sender fills the parameters from `others`, in the
                // order the registration numbers them (others_example's).
                others: waVariables,
                others_example: Object.fromEntries(
                    waVariables.map((v) => [v, waDraft.examples[v].trim()])
                ),
                meta_template_data: buildMetaTemplateData(waDraft, media),
                // Registered through AWS End User Messaging only; with no
                // platform the API took the direct Meta path.
                platform: 'aws',
            },
            () => navigate('/templates')
        )
    }

    const onSaveDesign = () => {
        if (submitting) return
        const basePayload = {
            template_name: templateName.trim(),
            comm_type: commType,
            comment,
            subject,
        }

        if (commType === EMAIL) {
            const errors = {
                ...validateTemplateForm({
                    commType,
                    templateName,
                    subject,
                    approverType,
                }),
                ...validateCategoryPair(category, subCategory, commType),
            }
            // The sender fills the calendar title with a strict substitute,
            // so an invite needs one.
            if (isCalendar && !calendarTitle.trim())
                errors.calendarTitle = 'Enter the calendar invite title'
            setFieldErrors(errors)
            if (Object.keys(errors).length) return
            const emailFields = {
                ...basePayload,
                attachments,
                approver_type: approverType,
                source_email: sourceEmail,
                source_email_header: sourceEmailHeader.trim(),
                reply_to: replyTo.trim(),
                is_calendar: isCalendar,
                calendar_title: isCalendar ? calendarTitle.trim() : '',
                others: variables,
            }
            if (htmlEmail && !isEdit) {
                if (replacementHtml === null) {
                    setSubmitError(
                        "Choose the email's .tmpl file, or build the email in the editor."
                    )
                    return
                }
                // The create stores `template_data` as the body; no design.
                submitTemplate(
                    {
                        ...emailFields,
                        template_data: replacementHtml,
                        design_json: '',
                    },
                    toTestPage
                )
                return
            }
            if (htmlEmail) {
                // An empty `body` makes the update store `template_data` in
                // the template's own file; a kept `body` leaves the HTML as
                // it is. A replaced HTML file no longer matches any old
                // design, so the design is cleared, as the old app does.
                submitTemplate(
                    replacementHtml === null
                        ? emailFields
                        : {
                              ...emailFields,
                              body: '',
                              template_data: replacementHtml,
                              design_json: '',
                          }
                )
                return
            }
            compileDesign(design).then(({ html, errors }) => {
                if (!html || errors.length) {
                    setSubmitError(
                        errors[0]?.message ??
                            'The email design failed to compile.'
                    )
                    return
                }
                submitTemplate(
                    {
                        ...emailFields,
                        // Empty on an edit, so the update writes the new HTML
                        // and design into the template's existing files.
                        ...(isEdit && { body: '' }),
                        template_data: html,
                        design_json: JSON.stringify(design),
                    },
                    toTestPage
                )
            })
        }

        if (commType === WHATSAPP) submitWhatsApp(basePayload)

        if (TEXT_CONTENT_TYPES.includes(commType)) {
            const form = {
                commType,
                templateName,
                comment,
                subject,
                approverType,
                text,
                dltTemplateId,
                others: variables,
            }
            const errors = {
                ...validateTemplateForm(form),
                ...validateCategoryPair(category, subCategory, commType),
            }
            setFieldErrors(errors)
            if (Object.keys(errors).length) return
            // On an edit `body` stays as saved: the update writes the text
            // into that same file.
            submitTemplate(buildTextTemplatePayload(form), () =>
                navigate('/templates')
            )
        }
    }

    /** Add the typed name to the template's variables (Enter, comma or Add). */
    const addVariable = () => {
        const name = variableDraft.trim().replace(/^\$\{(.*)\}$/, '$1')
        if (!name) return
        if (!VARIABLE_NAME_RE.test(name)) {
            setVariableError(
                'Use letters, digits and underscores, not starting with a digit'
            )
            return
        }
        if ((BUILT_IN_VARIABLES[commType] ?? []).includes(name)) {
            setVariableError(`${name} is filled in by the sender itself`)
            return
        }
        setVariableError('')
        setVariableDraft('')
        if (!extraVariables.includes(name))
            setExtraVariables([...extraVariables, name])
    }

    /** Add every chosen file as an attachment; one with the same content as another is skipped. */
    const addAttachments = async (event) => {
        const read = await handleFileRead(event)
        if (!read.length) return
        setAttachments((prev) => {
            const next = [...prev]
            read.forEach((file) => {
                if (!next.some((a) => a.encoded_file === file.encoded_file))
                    next.push(file)
            })
            return next
        })
    }

    const variablesField = (
        <Box>
            <Typography
                variant="overline"
                component="div"
                sx={{ color: TEXT.muted, lineHeight: 2 }}
            >
                Variables ({variables.length})
            </Typography>
            {variables.length > 0 && (
                <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}
                >
                    {variables.map((v) =>
                        foundVariables.includes(v) ? (
                            <Chip
                                key={v}
                                label={variableExample(commType, v)}
                                title="Found in the content"
                            />
                        ) : (
                            <Chip
                                key={v}
                                label={variableExample(commType, v)}
                                onDelete={() =>
                                    setExtraVariables(
                                        extraVariables.filter((x) => x !== v)
                                    )
                                }
                            />
                        )
                    )}
                </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                    label="Add a variable"
                    value={variableDraft}
                    onChange={(event) => {
                        setVariableDraft(event.target.value)
                        setVariableError('')
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ',') {
                            event.preventDefault()
                            addVariable()
                        }
                    }}
                    error={Boolean(variableError)}
                    helperText={
                        variableError ||
                        `Every value a send must supply. ${variableExample(
                            commType
                        )} placeholders in the content are listed on their own; add any other the send needs.`
                    }
                    sx={{ flex: 1 }}
                />
                <Button
                    variant="outlined"
                    onClick={addVariable}
                    sx={{ mt: 0.5 }}
                >
                    Add
                </Button>
            </Box>
        </Box>
    )

    // The saved address stays selectable even if it is no longer listed.
    const senderEmailOptions = [
        ...new Set([...senderEmails, sourceEmail].filter(Boolean)),
    ]

    /**
     * Read a chosen .tmpl file as the email's HTML: a new
     * email made from it, or the replacement of an edited one's. A new one
     * with no name yet is named after the file, as the old upload did.
     */
    const onChooseHtml = async (event) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        if (!/\.tmpl$/i.test(file.name)) {
            setSubmitError(
                `${file.name} is not a template file. Choose a .tmpl file.`
            )
            return
        }
        try {
            const html = await readText(file)
            if (!html.trim()) {
                setSubmitError(
                    `${file.name} is empty. Choose the email's .tmpl file.`
                )
                return
            }
            setReplacementHtml(withLineBreaksForSend(html))
            setReplacementName(file.name)
            setSubmitError('')
            if (!isEdit && !templateName.trim())
                editField(
                    'templateName',
                    setTemplateName
                )(
                    normaliseTemplateName(
                        EMAIL,
                        file.name.replace(/\.(html?|tmpl)$/i, '')
                    )
                )
        } catch {
            setSubmitError(`${file.name} could not be read. Choose it again.`)
        }
    }

    // The AI chat above the content (a new template only): its draft fills the
    // form. An email arrives as `content` (data) and is built as blocks; the
    // last content is kept for the next change, and the design it made is kept
    // to tell whether the blocks were changed by hand since.
    const aiContent = useRef(null)
    const aiDesign = useRef(null)
    const aiOn = !isEdit && !uploadHtml
    const applyAi = (result, { first }) => {
        if (first && !templateName.trim() && result.template_name)
            setTemplateName(normaliseTemplateName(commType, result.template_name))
        if (commType === EMAIL) {
            if (result.subject) setSubject(result.subject)
            const next = result.content
                ? designFromEmailContent(result.content)
                : designFromLegacyHtml(result.body)
            aiContent.current = result.content || null
            aiDesign.current = JSON.stringify(next)
            setDesign(next)
        } else {
            setText(result.body)
            // A push notification's title comes as its subject.
            if (commType === PUSH && result.subject) setSubject(result.subject)
            // WhatsApp: the AI's buttons fill the Buttons section (no list: kept as they are).
            if (commType === WHATSAPP && Array.isArray(result.buttons))
                changeWaDraft(
                    { buttons: buttonRowsFromAi(result.buttons) },
                    { buttonRows: true }
                )
        }
    }
    const aiCurrent = () =>
        commType === EMAIL
            ? { subject, content: aiContent.current }
            : commType === WHATSAPP
              ? { body: text, buttons: buttonsForAi(waDraft.buttons) }
              : commType === PUSH
                ? { subject, body: text }
                : { body: text }
    const handEdited =
        commType === EMAIL &&
        aiDesign.current !== null &&
        JSON.stringify(design) !== aiDesign.current

    // One button for every channel and mode: it saves, and the service checks the category and sub-category
    // (and the text) first.
    const submitLabel = 'Verify and Save'

    const submitButton = (label) => (
        <Button
            variant="contained"
            onClick={onSaveDesign}
            size="large"
            disabled={submitting}
            startIcon={
                submitting ? (
                    <CircularProgress size={16} sx={{ color: TEXT.muted }} />
                ) : null
            }
            sx={actionButtonSx}
        >
            {submitting ? 'Saving…' : label}
        </Button>
    )

    const approversPending = loadingApprovers && !approverTypes?.length
    const approverSelect = (
        <FormControl fullWidth error={Boolean(fieldErrors.approverType)}>
            <InputLabel required id="approver-type-label">
                Choose Approver type
            </InputLabel>
            <Select
                label="Choose Approver type"
                labelId="approver-type-label"
                value={approverType}
                onChange={(event) =>
                    editField(
                        'approverType',
                        setApproverType
                    )(event.target.value)
                }
            >
                {approverTypes?.map((approver, index) => (
                    <MenuItem key={index} value={approver}>
                        {approver}
                    </MenuItem>
                ))}
            </Select>
            {approversPending && (
                <FormHelperText>Loading approvers…</FormHelperText>
            )}
            {fieldErrors.approverType && (
                <FormHelperText>{fieldErrors.approverType}</FormHelperText>
            )}
        </FormControl>
    )

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: '1400px', mx: 'auto', p: { xs: 3, md: 5 } }}>
                {/* Page Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 4,
                    }}
                >
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: `${RADIUS.md}px`,
                            background: GRADIENT.brand,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {/* Deep Blue on the orange-to-yellow tile in both
                            themes (white is 3.3:1 on orange). */}
                        <EditNoteIcon
                            sx={{ color: ON_ORANGE, fontSize: ICON.xl }}
                        />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="h2"
                            component="h1"
                            sx={{ color: TEXT.primary, lineHeight: 1.2 }}
                        >
                            {isEdit ? 'Edit Template' : 'Create Template'}
                        </Typography>
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{
                                color: TEXT.muted,
                                mt: 0.3,
                            }}
                        >
                            {isEdit
                                ? templateName || 'Loading the template…'
                                : 'Build and configure a new communication template'}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            ml: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Button variant="outlined" onClick={onCancel}>
                            Cancel
                        </Button>
                        {load.state === 'ready' &&
                            !(isEdit && commType === WHATSAPP) &&
                            submitButton(submitLabel)}
                    </Box>
                </Box>

                {submitError && (
                    <Box sx={{ mb: 3 }}>
                        <ErrorBanner message={submitError} />
                    </Box>
                )}

                {tagMismatch && (
                    <Box sx={{ mb: 3 }}>
                        <TagMismatchPanel
                            detail={tagMismatch}
                            onUse={useSuggestedTags}
                            submitLabel={submitLabel}
                        />
                    </Box>
                )}

                {load.state === 'loading' && (
                    <Box
                        role="status"
                        sx={{ display: 'flex', justifyContent: 'center', p: 6 }}
                    >
                        <CircularProgress sx={{ color: BRAND.orange }} />
                    </Box>
                )}

                {load.state === 'error' && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 2,
                        }}
                    >
                        <ErrorBanner
                            message={`The template could not be loaded: ${load.reason}`}
                        />
                        <Button variant="outlined" onClick={loadTemplate}>
                            Try again
                        </Button>
                    </Box>
                )}

                {load.state === 'ready' && isEdit && commType === WHATSAPP && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            background: ACCENT.bg08,
                            border: `1px solid ${ACCENT.border30}`,
                            borderRadius: `${RADIUS.md}px`,
                            px: 2,
                            py: 1.25,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: TEXT.secondary }}
                        >
                            A WhatsApp template is registered with WhatsApp,
                            which reviews its content, and comms cannot send a
                            change to that registration yet. It keeps sending
                            exactly as it is. To change it, duplicate it into a
                            new WhatsApp template.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() =>
                                navigate('/create-template', {
                                    state: { channel: WHATSAPP, body: text },
                                })
                            }
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Duplicate as a new WhatsApp template
                        </Button>
                    </Box>
                )}

                {load.state === 'ready' && !(isEdit && commType === WHATSAPP) && (
                    <>
                        {/* Main Form Card */}
                        <Paper elevation={0} sx={{ ...cardSx, p: 3, mb: 3 }}>
                            <SectionTitle>Template Configuration</SectionTitle>
                            <Grid
                                container
                                columnSpacing={2}
                                rowSpacing={3}
                                alignItems="center"
                            >
                                <Grid item xs={12} sm={3}>
                                    <SingleSelect
                                        label="Choose Communication Type"
                                        size="medium"
                                        options={commTypes}
                                        optionLabelText="label"
                                        optionValueText="value"
                                        value={commType}
                                        handleChange={onChangeCommType}
                                        disabled={isEdit}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        required
                                        disabled={isEdit}
                                        error={Boolean(
                                            fieldErrors.templateName
                                        )}
                                        helperText={fieldErrors.templateName}
                                        // A helper line would push the row out of
                                        // line; the rule shows on hover instead.
                                        title={
                                            isEdit
                                                ? 'The name cannot change: other services find a template by its name'
                                                : commType === WHATSAPP
                                                ? 'Lowercase letters, digits and underscores: WhatsApp registers the name as it is'
                                                : 'Lowercase; spaces and hyphens become underscores'
                                        }
                                        label={
                                            commType === WHATSAPP
                                                ? 'Template ID'
                                                : 'Template Name'
                                        }
                                        variant="outlined"
                                        value={templateName}
                                        onChange={(event) =>
                                            editField(
                                                'templateName',
                                                setTemplateName
                                            )(
                                                normaliseTemplateName(
                                                    commType,
                                                    event.target.value
                                                )
                                            )
                                        }
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <SingleSelect
                                        label="Category"
                                        required
                                        size="medium"
                                        options={
                                            commType === WHATSAPP
                                                ? waCategoryOptions(commType)
                                                : categoriesForCommType(
                                                      commType
                                                  )
                                        }
                                        optionLabelText="label"
                                        optionValueText="value"
                                        value={category}
                                        error={Boolean(fieldErrors.category)}
                                        helperText={fieldErrors.category}
                                        handleChange={(v) => {
                                            editField(
                                                'category',
                                                setCategory
                                            )(v)
                                            // A sub-category that no longer fits is dropped.
                                            if (
                                                !subCategoriesForCategory(
                                                    v,
                                                    commType
                                                ).some(
                                                    (sc) =>
                                                        sc.value === subCategory
                                                )
                                            )
                                                setSubCategory('')
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <SingleSelect
                                        label="Sub-category"
                                        required
                                        size="medium"
                                        options={subCategoriesForCategory(
                                            category,
                                            commType
                                        )}
                                        optionLabelText="label"
                                        optionValueText="value"
                                        value={subCategory}
                                        disabled={!category}
                                        error={Boolean(fieldErrors.subCategory)}
                                        helperText={
                                            fieldErrors.subCategory ??
                                            (category
                                                ? undefined
                                                : 'Choose a category first')
                                        }
                                        handleChange={editField(
                                            'subCategory',
                                            setSubCategory
                                        )}
                                    />
                                </Grid>

                                {commType === SMS && (
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            required
                                            label="DLT template ID"
                                            variant="outlined"
                                            value={dltTemplateId}
                                            error={Boolean(
                                                fieldErrors.dltTemplateId
                                            )}
                                            helperText={
                                                fieldErrors.dltTemplateId ??
                                                'The ID registered on the DLT portal'
                                            }
                                            inputProps={{
                                                inputMode: 'numeric',
                                            }}
                                            onChange={(event) =>
                                                editField(
                                                    'dltTemplateId',
                                                    setDltTemplateId
                                                )(event.target.value)
                                            }
                                            fullWidth
                                        />
                                    </Grid>
                                )}

                                {TEXT_CONTENT_TYPES.includes(commType) && (
                                    <>
                                        {commType !== SMS && (
                                            <Grid item xs={12} sm={3}>
                                                {approverSelect}
                                            </Grid>
                                        )}
                                    </>
                                )}

                                {commType === EMAIL && (
                                    <Grid item xs={12} sm={3}>
                                        {approverSelect}
                                    </Grid>
                                )}

                                {commType === WHATSAPP && (
                                    <>
                                        <Grid item xs={12} sm={2}>
                                            <SingleSelect
                                                label="WhatsApp channel"
                                                size="medium"
                                                options={WA_CHANNELS}
                                                optionLabelText="label"
                                                optionValueText="value"
                                                value={waChannel}
                                                handleChange={setWaChannel}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            {approverSelect}
                                        </Grid>
                                    </>
                                )}

                                {commType === EMAIL && (
                                    <>
                                        <Grid item xs={12} sm={3}>
                                            <TextField
                                                label="Comment"
                                                variant="outlined"
                                                value={comment}
                                                onChange={(event) =>
                                                    setComment(
                                                        event.target.value
                                                    )
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                required
                                                error={Boolean(
                                                    fieldErrors.subject
                                                )}
                                                helperText={fieldErrors.subject}
                                                label="Subject"
                                                variant="outlined"
                                                value={subject}
                                                onChange={(event) =>
                                                    editField(
                                                        'subject',
                                                        setSubject
                                                    )(event.target.value)
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                    </>
                                )}

                                {commType === EMAIL && (
                                    <Grid item xs={12}>
                                        {/* Top-aligned: fields with a helper line are taller. */}
                                        <Grid
                                            container
                                            spacing={2}
                                            alignItems="flex-start"
                                        >
                                            <Grid item xs={12} sm={4}>
                                                <FormControl fullWidth>
                                                    <InputLabel id="sender-email-label">
                                                        Sender email
                                                    </InputLabel>
                                                    <Select
                                                        label="Sender email"
                                                        labelId="sender-email-label"
                                                        value={sourceEmail}
                                                        onChange={(event) =>
                                                            setSourceEmail(
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                    >
                                                        {senderEmailOptions.map(
                                                            (email) => (
                                                                <MenuItem
                                                                    key={email}
                                                                    value={
                                                                        email
                                                                    }
                                                                >
                                                                    {email}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </Select>
                                                </FormControl>
                                                <Button
                                                    size="small"
                                                    onClick={() =>
                                                        setManagingSenders(true)
                                                    }
                                                    sx={{ mt: 0.5, px: 0.5 }}
                                                >
                                                    Manage sender emails
                                                </Button>
                                                <Dialog
                                                    open={managingSenders}
                                                    onClose={() =>
                                                        setManagingSenders(
                                                            false
                                                        )
                                                    }
                                                    fullWidth
                                                    maxWidth="sm"
                                                >
                                                    <DialogContent>
                                                        <SenderEmails
                                                            onChange={
                                                                loadSenderEmails
                                                            }
                                                        />
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent:
                                                                    'flex-end',
                                                                mt: 2,
                                                            }}
                                                        >
                                                            <Button
                                                                variant="outlined"
                                                                onClick={() =>
                                                                    setManagingSenders(
                                                                        false
                                                                    )
                                                                }
                                                            >
                                                                Done
                                                            </Button>
                                                        </Box>
                                                    </DialogContent>
                                                </Dialog>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    label="Sender name"
                                                    placeholder="e.g. Bhanzu Learning Center"
                                                    value={sourceEmailHeader}
                                                    onChange={(event) =>
                                                        setSourceEmailHeader(
                                                            event.target.value
                                                        )
                                                    }
                                                    helperText="Shown as who the email is from"
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    label="Reply-to"
                                                    placeholder="Bhanzu Support <help@bhanzu.com>"
                                                    value={replyTo}
                                                    onChange={(event) =>
                                                        setReplyTo(
                                                            event.target.value
                                                        )
                                                    }
                                                    helperText="Empty: replies go to Bhanzu Support <help@bhanzu.com>"
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={isCalendar}
                                                            onChange={(
                                                                event
                                                            ) => {
                                                                setIsCalendar(
                                                                    event.target
                                                                        .checked
                                                                )
                                                                editField(
                                                                    'calendarTitle',
                                                                    setCalendarTitle
                                                                )(
                                                                    event.target
                                                                        .checked
                                                                        ? calendarTitle
                                                                        : ''
                                                                )
                                                            }}
                                                        />
                                                    }
                                                    label="Attach a calendar invite"
                                                    sx={{
                                                        color: TEXT.secondary,
                                                    }}
                                                />
                                            </Grid>
                                            {isCalendar && (
                                                <Grid item xs={12} sm={8}>
                                                    <TextField
                                                        required
                                                        label="Calendar invite title"
                                                        value={calendarTitle}
                                                        error={Boolean(
                                                            fieldErrors.calendarTitle
                                                        )}
                                                        helperText={
                                                            fieldErrors.calendarTitle ??
                                                            `Can use placeholders such as ${variableExample(
                                                                EMAIL,
                                                                'student_name'
                                                            )}`
                                                        }
                                                        onChange={(event) =>
                                                            editField(
                                                                'calendarTitle',
                                                                setCalendarTitle
                                                            )(
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        fullWidth
                                                    />
                                                </Grid>
                                            )}
                                            <Grid item xs={12}>
                                                {variablesField}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>

                        {aiOn && (
                            <AiAssistant
                                key={commType}
                                channel={commType}
                                getCurrent={aiCurrent}
                                onApply={applyAi}
                                handEdited={handEdited}
                                variables={variables}
                                // An uploaded .tmpl file is the content: the AI writes editor blocks.
                                unavailable={
                                    htmlEmail
                                        ? 'Writing with AI works in the editor. Choose Build it in the editor to use it.'
                                        : ''
                                }
                            />
                        )}

                        {commType === WHATSAPP && (
                            <WhatsAppContent
                                body={text}
                                onBodyChange={changeText}
                                draft={waDraft}
                                onDraftChange={changeWaDraft}
                                variables={waVariables}
                                category={category}
                                marks={waMarks}
                            />
                        )}

                        {/* SMS / Voice / Push / Slack content */}
                        {TEXT_CONTENT_TYPES.includes(commType) && (
                            <Paper
                                elevation={0}
                                sx={{ ...cardSx, p: 3, mb: 3 }}
                            >
                                <SectionTitle>
                                    {commType === VOICE
                                        ? 'Voice Script'
                                        : commType === PUSH
                                        ? 'Notification Content'
                                        : commType === SMS
                                        ? 'SMS Text'
                                        : 'Slack Message'}
                                </SectionTitle>
                                {commType === PUSH && (
                                    <TextField
                                        required
                                        label="Notification title"
                                        variant="outlined"
                                        value={subject}
                                        error={Boolean(fieldErrors.subject)}
                                        helperText={
                                            fieldErrors.subject ??
                                            'Shown in bold above the message'
                                        }
                                        onChange={(event) =>
                                            editField(
                                                'subject',
                                                setSubject
                                            )(event.target.value)
                                        }
                                        sx={{ mb: 2 }}
                                        fullWidth
                                    />
                                )}
                                <TextField
                                    multiline
                                    rows={10}
                                    value={text}
                                    onChange={handleTextChange}
                                    error={Boolean(fieldErrors.text)}
                                    inputProps={{
                                        'aria-label':
                                            commType === VOICE
                                                ? 'Voice script'
                                                : commType === PUSH
                                                ? 'Notification body'
                                                : commType === SMS
                                                ? 'SMS text'
                                                : 'Slack message',
                                    }}
                                    placeholder={
                                        commType === VOICE
                                            ? `Hello ${variableExample(
                                                  VOICE
                                              )}, your class starts at ${variableExample(
                                                  VOICE,
                                                  'class_time'
                                              )}.`
                                            : commType === PUSH
                                            ? `Notification body. Use ${variableExample(
                                                  PUSH
                                              )} for personalization.`
                                            : commType === SMS
                                            ? `SMS text, exactly as registered on DLT. Use ${variableExample(
                                                  SMS
                                              )} for variables.`
                                            : `Slack message (mrkdwn supported). Use ${variableExample(
                                                  SLACK
                                              )} for personalization.`
                                    }
                                    helperText={
                                        fieldErrors.text ??
                                        (commType === VOICE
                                            ? 'Wrapped in <speak> tags automatically.'
                                            : `Variables are written ${variableExample(
                                                  commType
                                              )}; {{name}} would be sent as literal text.`)
                                    }
                                    fullWidth
                                />
                                <Box sx={{ mt: 3 }}>{variablesField}</Box>
                            </Paper>
                        )}

                        {/* Attachments & Email Editor */}
                        {commType === EMAIL && (
                            <Paper elevation={0} sx={{ ...cardSx, p: 3 }}>
                                <SectionTitle>Email Content</SectionTitle>
                                <Typography
                                    variant="overline"
                                    component="div"
                                    sx={{ color: TEXT.muted, lineHeight: 2 }}
                                >
                                    Attachments ({attachments.length})
                                </Typography>
                                {attachments.length > 0 && (
                                    <Box
                                        display="flex"
                                        sx={{
                                            mb: 2,
                                            flexWrap: 'wrap',
                                            gap: 1.5,
                                        }}
                                    >
                                        {attachments.map(
                                            (attachment, index) => {
                                                // A saved attachment has `name`, a new one `file_name`.
                                                const label =
                                                    attachment.name ??
                                                    attachment.file_name
                                                return (
                                                    <Box
                                                        key={`${label}-${index}`}
                                                        display="flex"
                                                        alignItems="center"
                                                        sx={{
                                                            background:
                                                                ACCENT.bg12,
                                                            border: `1px solid ${ACCENT.border30}`,
                                                            borderRadius: `${RADIUS.pill}px`,
                                                            pl: 1.5,
                                                            pr: 0.5,
                                                            py: 0.25,
                                                            color: ACCENT.text,
                                                            typography:
                                                                'caption',
                                                        }}
                                                    >
                                                        <InsertDriveFileIcon
                                                            sx={{
                                                                mr: 0.75,
                                                                fontSize:
                                                                    ICON.md,
                                                            }}
                                                        />
                                                        {label}
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Remove attachment ${label}`}
                                                            onClick={() =>
                                                                setAttachments(
                                                                    attachments.filter(
                                                                        (
                                                                            _,
                                                                            i
                                                                        ) =>
                                                                            i !==
                                                                            index
                                                                    )
                                                                )
                                                            }
                                                            sx={{
                                                                ml: 0.5,
                                                                color: ACCENT.text,
                                                            }}
                                                        >
                                                            <CloseIcon
                                                                sx={{
                                                                    fontSize:
                                                                        ICON.sm,
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Box>
                                                )
                                            }
                                        )}
                                    </Box>
                                )}
                                <Button
                                    variant="outlined"
                                    startIcon={<AttachFileIcon />}
                                    component="label"
                                    sx={{
                                        mb: 3,
                                        borderColor: ACCENT.border55,
                                        color: ACCENT.text,
                                        '&:hover': {
                                            borderColor: BRAND.orange,
                                            background: ACCENT.bg08,
                                            color: ACCENT.text,
                                        },
                                    }}
                                >
                                    Add attachments
                                    {/* Any type, several at once: the API stores any
                                extension (calendar files, documents, sheets). */}
                                    <input
                                        hidden
                                        multiple
                                        type="file"
                                        aria-label="Attachment files"
                                        onChange={addAttachments}
                                    />
                                </Button>
                                {!isEdit && (
                                    <RadioGroup
                                        row
                                        aria-label="How the email is made"
                                        value={emailSource}
                                        onChange={(event) => {
                                            setEmailSource(event.target.value)
                                            setSubmitError('')
                                        }}
                                        sx={{ mb: 2 }}
                                    >
                                        <FormControlLabel
                                            value={BUILDER}
                                            control={<Radio />}
                                            label="Build it in the editor"
                                            sx={{ color: TEXT.secondary }}
                                        />
                                        <FormControlLabel
                                            value={HTML_FILE}
                                            control={<Radio />}
                                            label="Upload a .tmpl file"
                                            sx={{ color: TEXT.secondary }}
                                        />
                                    </RadioGroup>
                                )}
                                {htmlEmail ? (
                                    <EmailHtmlPanel
                                        isEdit={isEdit}
                                        html={emailHtml}
                                        fileName={replacementName}
                                        onChooseFile={onChooseHtml}
                                        onKeepSaved={() => {
                                            setReplacementHtml(null)
                                            setReplacementName('')
                                        }}
                                        onDuplicate={() =>
                                            navigate('/create-template', {
                                                state: {
                                                    channel: EMAIL,
                                                    body: emailHtml,
                                                    subject,
                                                },
                                            })
                                        }
                                    />
                                ) : (
                                    <EmailBuilder
                                        value={design}
                                        onChange={setDesign}
                                    />
                                )}
                            </Paper>
                        )}

                        {/* Standalone attachments display for non-email types */}
                        {commType !== EMAIL && attachments.length > 0 && (
                            <Box
                                display="flex"
                                sx={{ mb: 3, flexWrap: 'wrap', gap: 1.5 }}
                            >
                                {attachments.map(({ file_name }) => (
                                    <Box
                                        key={file_name}
                                        display="flex"
                                        alignItems="center"
                                        sx={{
                                            background: ACCENT.bg08,
                                            border: `1px solid ${ACCENT.border30}`,
                                            borderRadius: `${RADIUS.pill}px`,
                                            px: 1.5,
                                            py: 0.5,
                                            color: ACCENT.text,
                                            typography: 'caption',
                                        }}
                                    >
                                        <InsertDriveFileIcon
                                            sx={{ mr: 0.75, fontSize: ICON.md }}
                                        />
                                        {file_name}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </Box>
            <ConfirmDialog
                open={confirmLeave}
                title="Leave this page?"
                message="What you entered here is not saved and is lost if you leave."
                confirmLabel="Leave without saving"
                destructive
                onConfirm={() => {
                    setConfirmLeave(false)
                    leave()
                }}
                onCancel={() => setConfirmLeave(false)}
            />
        </Box>
    )
}

TemplateForm.propTypes = {
    // The template to edit; none creates a new one.
    templateId: PropTypes.string,
    // A new email starts on "Upload a .tmpl file" (the Upload Template page).
    uploadHtml: PropTypes.bool,
}

export default TemplateForm
